import { useState } from 'react'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { Button } from '@/ui/Button'
import { InlineError } from '@/ui/states'
import { PublicPage } from './PublicPage'
import { useToken } from './useToken'

/**
 * `/outreach/unsubscribe` — the one-click link §8 puts in every outreach message.
 *
 * The **sixth** public page, and the only one whose reader has no relationship
 * with AugRank at all. They were contacted by AugRank's *customer*, so every
 * word here names the organisation rather than the product: a page that said
 * "you have been unsubscribed from AugRank" would be both wrong and alarming to
 * somebody who has never heard of it.
 *
 * Same two rules as the other five, for the same reasons: **no guard**, because
 * the link is opened by whoever holds the mailbox on a device that may not be
 * theirs; and it acts on a **POST**, never on page load, because mail security
 * scanners fetch every URL in a message before a human sees it — and a GET that
 * suppressed would mean every message a tenant sends unsubscribing its own
 * recipient on arrival.
 *
 * **It asks first, and this one really does have to.** The other pages redeem a
 * single-use token the person has already decided to spend. This one is the
 * opposite: the token never expires and is not single-use, so a scanner cannot
 * burn it — but the *effect* is somebody being permanently removed from a list,
 * and doing that from a preview pane's fetch would be the failure the POST rule
 * exists to prevent.
 *
 * **Pressing it twice is a success, not an error.** The API answers 200 with
 * `already: true`, and the page says so. An unsubscribe link that reported an
 * error is one the recipient cannot tell from a link that did not work, and the
 * next thing they do is complain.
 */
export function OutreachUnsubscribe(): JSX.Element {
  const token = useToken()
  const [done, setDone] = useState<{ organisation: string; value: string } | null>(null)

  const unsubscribe = useMutation((value: string) =>
    call(() => api.POST('/api/v1/outreach/unsubscribe', { params: { query: { token: value } } })),
  )

  if (!token) {
    return (
      <PublicPage title="That link is incomplete">
        <div className="ar-card__body ar-stack">
          <p style={{ marginTop: 0 }}>
            The unsubscribe link needs the code from the message. Copy the whole address out of the
            email, or simply reply to it asking to be removed — that works too.
          </p>
        </div>
      </PublicPage>
    )
  }

  if (done) {
    return (
      <PublicPage title="You are unsubscribed">
        <div className="ar-card__body ar-stack">
          <p style={{ marginTop: 0 }}>
            <strong>{done.organisation}</strong> will not contact <strong>{done.value}</strong>{' '}
            again.
          </p>
          <p className="ar-muted">
            This applies to every channel they reach you on — email, WhatsApp, SMS and LinkedIn —
            and it takes effect immediately. If a message was already on its way when you pressed
            the button, it may still arrive.
          </p>
          <p className="ar-muted">
            AugRank is the software {done.organisation} uses to send this. We have added you to
            their do-not-contact list and hold nothing else about you.
          </p>
        </div>
      </PublicPage>
    )
  }

  return (
    <PublicPage title="Stop receiving these messages?">
      <div className="ar-card__body ar-stack">
        <InlineError failure={unsubscribe.state.failure} />
        <p style={{ marginTop: 0 }}>
          Press the button and the organisation that sent you this will stop contacting you, on
          every channel. It takes effect straight away and cannot be undone from here.
        </p>
        <p className="ar-muted">
          Nothing happens until you press it — opening this page does not unsubscribe you, so a link
          your mail provider checked automatically has not already acted.
        </p>
      </div>
      <div className="ar-card__footer">
        <span className="ar-card__spacer" />
        <Button
          tone="danger"
          busy={unsubscribe.pending}
          busyLabel="Unsubscribing"
          onClick={() => {
            void (async () => {
              const result = await unsubscribe.run(token)
              if (result.ok) {
                setDone({ organisation: result.data.organisation, value: result.data.value })
              }
            })()
          }}
        >
          Unsubscribe me
        </Button>
      </div>
    </PublicPage>
  )
}
